import type { Capability, Feature, Grants } from '@emr/shared';
import { decide, isEntitled } from '@emr/shared';

/**
 * What the navigation offers, and to whom.
 *
 * **This module is the point of the `shared/` workspace.** `docs/comparison-emrx.md` §4.1 recorded
 * the uncomfortable finding that every decision module in `shared/` was, at the time, called by
 * nothing outside its own tests — "our claim to have one authorization chokepoint is currently
 * true only vacuously". The web app calling `decide` and `isEntitled` directly, rather than
 * re-deriving the same answers from a role string, is what makes the claim non-vacuous on this
 * side: the UI cannot offer something the server would refuse, because the same function answered
 * both questions.
 *
 * The two gates are **not** interchangeable, and the difference is visible in the output type
 * rather than only in a comment. `shared/src/authorization.ts` is emphatic about this: a user who
 * lacks a capability by role must never be told to upgrade, because the upgrade would not grant it
 * and the message sends a practice to buy the wrong fix; and a practice that has not licensed a
 * feature must not see "you do not have permission", which sends an administrator hunting for a
 * role change that does not exist.
 *
 * So:
 *
 *   - **Capability missing → `hidden`.** The item is not part of this person's job. Showing it
 *     greyed out with an explanation is noise on every page load.
 *   - **Entitlement missing → `upgrade`.** The item is part of the job; the practice has not bought
 *     it. It stays visible and disabled, with upgrade-shaped wording, because someone has to be
 *     able to discover that the capability exists in order to buy it.
 *
 * Order matters: capability is checked first, so a role that has no business with a feature never
 * sees an upgrade prompt for it.
 */

export interface NavItem {
  readonly to: string;
  /** A key under `shell.json`'s `nav.*` (`useTranslation('shell')` in `AppShell.tsx`'s
   * `NavItemList`), not the label text itself — `BACKLOG.md` B7. Named `labelKey` rather than
   * `label` so a reader cannot mistake it for display text already resolved to a locale. */
  readonly labelKey: string;
  /** Capability required to use this destination. `null` for anything a member may open. */
  readonly capability: Capability | null;
  /** Plan feature required. `null` for anything included on every tier. */
  readonly feature: Feature | null;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', labelKey: 'nav.overview', capability: null, feature: null },
  // `patient:read` is core clinical function, not a commercial add-on — no `feature` gate. Front
  // desk and billing hold it without `chart:read`; `routes/patients/PatientChartPage.tsx` gates the
  // clinical tabs underneath separately, inline, once a specific chart is open.
  { to: '/patients', labelKey: 'nav.patients', capability: 'patient:read', feature: null },
  // `schedule:manage` (`server/src/scheduling/routes.ts`'s own `manage` middleware) is the only
  // capability that surface defines — "book, move, and cancel appointments" — held by every
  // clinical role plus `practice_admin` and `front_desk`, and by no billing-only or audit-only
  // role. No feature/tier gate: scheduling ships on every plan.
  { to: '/schedule', labelKey: 'nav.schedule', capability: 'schedule:manage', feature: null },
  // Staff secure-messaging inbox (`BACKLOG.md` W2, pillar 3, `docs/decisions/patient-portal-
  // messaging-scope.md`). `patient:read` — the same gate `messaging/routes.ts` itself uses — rather
  // than `chart:read`: `front_desk`/`billing` hold this without holding `chart:read`, and both are
  // exactly the staff the decision doc's own "general inbox" routing reasoning names as the intended
  // audience for the unassigned queue. No `feature` gate: this ships on every plan, matching
  // scheduling's own posture above.
  { to: '/inbox', labelKey: 'nav.inbox', capability: 'patient:read', feature: null },
  { to: '/access', labelKey: 'nav.yourAccess', capability: null, feature: null },
  // Self-service profile data (a mark, plus the admin-set signature line displayed alongside it),
  // not clinical content — `capability: null`, same posture as `/access` above, matching
  // `server/src/auth/signatureRoutes.ts`'s own "no capability beyond an active session" reasoning for the
  // self-service half of that route file. The admin-only colleague editor on this same page checks
  // `org:manage_members` itself rather than needing a second nav entry.
  { to: '/signature', labelKey: 'nav.signature', capability: null, feature: null },
  // Practice colour scheme and (on the `whiteLabel`-entitled `enterprise` tier) logo
  // (`BACKLOG.md` B4). `org:manage_settings` — the same capability `server/src/orgs/
  // brandingRoutes.ts` gates on — held only by `practice_admin`. No `feature` gate here: a lower
  // tier can still open this page and set its colour scheme, only the logo section inside is
  // itself feature-gated (`PracticeBrandingPage.tsx`'s own `LogoSection`), so hiding the whole nav
  // item behind `whiteLabel` would hide the part every tier actually gets.
  { to: '/branding', labelKey: 'nav.branding', capability: 'org:manage_settings', feature: null },
  // Always visible — it is a catalog page, not itself entitlement-gated. `BACKLOG.md` M3/M5-M22:
  // dermatology is the one real, licensable entry; the other eighteen are visible "coming soon"
  // placeholders with nothing to gate. Individual entitlement is decided per-row inside the page
  // (`SpecialtyModulesPage.tsx`), the same "a route cannot rely on nav being hidden" reasoning
  // `GatedPages.tsx` already states for its own routes.
  {
    to: '/specialty-modules',
    labelKey: 'nav.specialtyModules',
    capability: null,
    feature: null,
  },
  // `analytics` is a commercial feature: `starter` does not include it, `growth` and `enterprise`
  // do. Entitlement, not permission.
  { to: '/analytics', labelKey: 'nav.analytics', capability: null, feature: 'analytics' },
  // `audit:read` is held by `practice_admin` and `auditor` and by no clinical role. Permission,
  // not entitlement — no plan change grants it.
  { to: '/audit', labelKey: 'nav.auditLog', capability: 'audit:read', feature: null },
  // `platform:manage_practices` is held only by `isPlatformOperator: true` — never by any org role,
  // however senior (`shared/src/authorization.ts`'s own `decide()`). This item is therefore hidden
  // for every ordinary practice member, including a `practice_admin`, and visible only to our own
  // staff. `navAvailability` needs no change to make this correct: `decide()` already branches
  // platform capabilities on `Grants.isPlatformOperator` regardless of org context.
  {
    to: '/platform',
    labelKey: 'nav.platform',
    capability: 'platform:manage_practices',
    feature: null,
  },
  // Roll-up billing across a reseller's managed children (`BACKLOG.md` T17). `org:read_children`
  // is held only by `reseller_admin` (`shared/src/authorization.ts`'s own `ROLE_CAPABILITIES`), so
  // this item is hidden for every ordinary practice role, including `practice_admin` — the same
  // "capability, not entitlement" register `nav.auditLog` above already occupies, no `feature` gate
  // since roll-up billing is not a commercial add-on.
  {
    to: '/reseller/rollup-statement',
    labelKey: 'nav.resellerRollup',
    capability: 'org:read_children',
    feature: null,
  },
  // Form-template builder (`BACKLOG.md` C10b) — the authoring surface, gated on
  // `org:manage_settings` per `authorization.ts`'s own description of that capability (it names
  // "templates" explicitly). No `feature` gate: this is core documentation infrastructure, not a
  // tier-gated commercial add-on — `server/src/forms/formSubmissionsRoutes.ts`'s own header argues
  // the identical point for the patient-scoped fill surface, reachable from a patient's chart tabs
  // instead of from this top-level nav.
  {
    to: '/form-templates',
    labelKey: 'nav.formTemplates',
    capability: 'org:manage_settings',
    feature: null,
  },
  // Google Calendar / Outlook Calendar two-way sync (`BACKLOG.md` K7). `capability: null` — mostly
  // self-service (connecting one's own calendar), the same "not clinical content, no capability"
  // register `/signature` above occupies; the BAA-upload and conflicts sections inside the page are
  // gated inline, per-section, on `org:manage_settings`/`schedule:manage` respectively
  // (`ProviderCalendarSyncPage.tsx`'s own header), the same shape `PracticeBrandingPage.tsx`'s
  // `LogoSection` already uses for a page that is mostly open with one gated part. No `feature`
  // gate: sync ships on every plan, matching scheduling's own posture.
  { to: '/calendar-sync', labelKey: 'nav.calendarSync', capability: null, feature: null },
];

export type NavAvailability =
  | { readonly kind: 'available' }
  | { readonly kind: 'hidden' }
  | { readonly kind: 'upgrade'; readonly feature: Feature };

export interface NavViewer {
  readonly grants: Grants;
  /** The practice's plan tier, as stored. A `string` because it comes from the database. */
  readonly tier: string;
}

export function navAvailability(item: NavItem, viewer: NavViewer): NavAvailability {
  if (item.capability !== null && !decide(viewer.grants, item.capability)) {
    return { kind: 'hidden' };
  }
  if (item.feature !== null && !isEntitled(viewer.tier, item.feature)) {
    return { kind: 'upgrade', feature: item.feature };
  }
  return { kind: 'available' };
}

export interface ResolvedNavItem {
  readonly item: NavItem;
  readonly availability: NavAvailability;
}

/** Every item this viewer should see, with `hidden` ones already removed. */
export function visibleNavItems(viewer: NavViewer): readonly ResolvedNavItem[] {
  return NAV_ITEMS.map((item) => ({ item, availability: navAvailability(item, viewer) })).filter(
    (resolved) => resolved.availability.kind !== 'hidden',
  );
}
